import { type KeyboardEvent, type PointerEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export const AVATAR_SIZE = 256;
/** 정착했을 때 틀이 차지하는 화면 비율 */
const FRAME = 0.78;
const MAX_ZOOM = 10;
const SETTLE_MS = 220;

/** 원본 사진 좌표(px)의 정사각형 */
interface Crop {
  x: number;
  y: number;
  s: number;
}
/** 원본 좌표 → 화면 좌표: screen = t + k * natural */
interface View {
  k: number;
  tx: number;
  ty: number;
}
type Corner = "nw" | "ne" | "sw" | "se";

type Gesture =
  | { kind: "pan"; startX: number; startY: number; crop: Crop; k: number }
  | { kind: "resize"; corner: Corner; ax: number; ay: number; view: View }
  | { kind: "pinch"; startDist: number; crop: Crop };

const clampN = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * 프로필 사진 자르기. 틀을 고르면 그 영역을 256×256으로 내보낸다.
 * - 끌기: 사진이 틀 아래에서 움직인다
 * - 모서리 끌기: 틀 크기 조절 → 손을 떼면 그 영역이 틀을 채우도록 자동 줌
 * - 휠·핀치·슬라이더·키보드(화살표, +/-): 확대·축소
 */
export function AvatarCropper({ file, onCancel, onSave, saving }: { file: File; onCancel: () => void; onSave: (blob: Blob) => void; saving: boolean }) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [size, setSize] = useState(0);
  const [crop, setCrop] = useState<Crop | null>(null);
  const [view, setView] = useState<View | null>(null);
  const [settling, setSettling] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const vpRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLCanvasElement>(null);
  const gesture = useRef<Gesture | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());

  // 사진 읽기
  useEffect(() => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => setImg(image);
    image.onerror = () => setError("이 사진은 열 수 없습니다. JPG, PNG, WebP 파일을 골라주세요.");
    image.src = url;
    return () => {
      // 주소를 해제하면 읽던 이미지가 onerror를 낸다. 그 전에 콜백을 끊는다.
      image.onload = image.onerror = null;
      URL.revokeObjectURL(url);
    };
  }, [file]);

  // 화면 크기: 대화상자 폭에 맞추되 최대 360px
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setSize(Math.min(360, Math.floor(entry!.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const w = img?.naturalWidth ?? 0;
  const h = img?.naturalHeight ?? 0;
  const minDim = Math.min(w, h);
  const minS = Math.min(minDim, Math.max(16, minDim / MAX_ZOOM));

  const fit = useCallback((c: Crop): View => {
    const k = (FRAME * size) / c.s;
    return { k, tx: size / 2 - k * (c.x + c.s / 2), ty: size / 2 - k * (c.y + c.s / 2) };
  }, [size]);

  const clamp = useCallback(
    (c: Crop): Crop => {
      const s = clampN(c.s, minS, minDim);
      return { s, x: clampN(c.x, 0, w - s), y: clampN(c.y, 0, h - s) };
    },
    [minS, minDim, w, h],
  );

  // 처음: 가운데 가장 큰 정사각형
  useEffect(() => {
    if (!img || !size) return;
    setCrop((prev) => {
      const c = prev ?? { x: (w - minDim) / 2, y: (h - minDim) / 2, s: minDim };
      setView(fit(c));
      return c;
    });
  }, [img, size, w, h, minDim, fit]);

  // 미리보기
  useEffect(() => {
    const cv = previewRef.current;
    if (!cv || !img || !crop) return;
    const raf = requestAnimationFrame(() => {
      const g = cv.getContext("2d")!;
      g.imageSmoothingQuality = "high";
      g.clearRect(0, 0, cv.width, cv.height);
      g.drawImage(img, crop.x, crop.y, crop.s, crop.s, 0, 0, cv.width, cv.height);
    });
    return () => cancelAnimationFrame(raf);
  }, [img, crop]);

  /** 틀 중심을 유지하며 크기만 바꾸고, 화면을 다시 맞춘다 (사진이 커지거나 작아져 보임) */
  const zoomTo = useCallback(
    (s: number) => {
      if (!crop) return;
      const cx = crop.x + crop.s / 2;
      const cy = crop.y + crop.s / 2;
      const next = clamp({ s, x: cx - s / 2, y: cy - s / 2 });
      setSettling(false);
      setCrop(next);
      setView(fit(next));
    },
    [crop, clamp, fit],
  );

  // 휠 확대·축소 (페이지 스크롤을 막아야 해서 passive가 아닌 리스너)
  useEffect(() => {
    const el = vpRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!crop) return;
      e.preventDefault();
      zoomTo(crop.s * Math.exp(e.deltaY * 0.0015));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [crop, zoomTo]);

  const settle = (c: Crop) => {
    setSettling(true);
    setView(fit(c));
    window.setTimeout(() => setSettling(false), SETTLE_MS);
  };

  const local = (e: PointerEvent) => {
    const r = vpRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!crop || !view) return;
    vpRef.current!.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, local(e));
    setSettling(false);
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = { kind: "pinch", startDist: Math.hypot(a!.x - b!.x, a!.y - b!.y), crop };
      return;
    }
    const corner = (e.target as HTMLElement).dataset.corner as Corner | undefined;
    if (corner) {
      // 반대쪽 모서리를 고정점으로
      const ax = corner.includes("w") ? crop.x + crop.s : crop.x;
      const ay = corner.includes("n") ? crop.y + crop.s : crop.y;
      gesture.current = { kind: "resize", corner, ax, ay, view };
    } else {
      const p = local(e);
      gesture.current = { kind: "pan", startX: p.x, startY: p.y, crop, k: view.k };
    }
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    if (!g || !pointers.current.has(e.pointerId)) return;
    const p = local(e);
    pointers.current.set(e.pointerId, p);

    if (g.kind === "pinch") {
      if (pointers.current.size < 2) return;
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      zoomTo((g.crop.s * g.startDist) / Math.max(dist, 1));
    } else if (g.kind === "pan") {
      // 사진을 끈 방향으로 움직이려면 틀(원본 좌표)은 반대로
      const next = clamp({ ...g.crop, x: g.crop.x - (p.x - g.startX) / g.k, y: g.crop.y - (p.y - g.startY) / g.k });
      setCrop(next);
      setView(fit(next));
    } else {
      // 모서리: 화면은 그대로, 틀만 바뀐다
      const nx = (p.x - g.view.tx) / g.view.k;
      const ny = (p.y - g.view.ty) / g.view.k;
      const west = g.corner.includes("w");
      const north = g.corner.includes("n");
      const room = Math.min(west ? g.ax : w - g.ax, north ? g.ay : h - g.ay);
      // 가로·세로 이동의 평균: 한쪽으로만 끌어도 틀이 자연스럽게 커지고 작아진다
      const s = clampN((Math.abs(nx - g.ax) + Math.abs(ny - g.ay)) / 2, minS, room);
      setCrop({ s, x: west ? g.ax - s : g.ax, y: north ? g.ay - s : g.ay });
    }
  };

  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (pointers.current.size > 0) {
      // 핀치에서 손가락 하나만 뗀 경우: 남은 손가락으로 이어서 끌기
      const [rest] = [...pointers.current.values()];
      if (crop && view) gesture.current = { kind: "pan", startX: rest!.x, startY: rest!.y, crop, k: view.k };
      return;
    }
    gesture.current = null;
    if (g?.kind === "resize" && crop) settle(crop);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!crop) return;
    const step = crop.s * 0.05;
    const moves: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    if (moves[e.key]) {
      e.preventDefault();
      const [dx, dy] = moves[e.key]!;
      const next = clamp({ ...crop, x: crop.x + dx, y: crop.y + dy });
      setCrop(next);
      setView(fit(next));
    } else if (e.key === "+" || e.key === "=") {
      e.preventDefault();
      zoomTo(crop.s / 1.15);
    } else if (e.key === "-") {
      e.preventDefault();
      zoomTo(crop.s * 1.15);
    }
  };

  const save = () => {
    if (!img || !crop) return;
    const cv = document.createElement("canvas");
    cv.width = AVATAR_SIZE;
    cv.height = AVATAR_SIZE;
    const g = cv.getContext("2d")!;
    g.imageSmoothingQuality = "high";
    g.drawImage(img, crop.x, crop.y, crop.s, crop.s, 0, 0, AVATAR_SIZE, AVATAR_SIZE);
    // WebP를 못 만드는 브라우저는 PNG로 (서버가 다시 인코딩한다)
    cv.toBlob((blob) => blob && onSave(blob), "image/webp", 0.92);
  };

  // 슬라이더: 로그 눈금 0~1000 ↔ 확대 1x~최대
  const maxZoom = minDim / minS || 1;
  const zoom = crop ? minDim / crop.s : 1;
  const slider = maxZoom > 1 ? Math.round((Math.log(zoom) / Math.log(maxZoom)) * 1000) : 0;

  const transition = settling ? `transform ${SETTLE_MS}ms ease, left ${SETTLE_MS}ms ease, top ${SETTLE_MS}ms ease, width ${SETTLE_MS}ms ease, height ${SETTLE_MS}ms ease` : "none";

  return (
    <div className="cropper" ref={wrapRef}>
      {error ? (
        <p className="form-error">{error}</p>
      ) : (
        <>
          <div
            ref={vpRef}
            className="crop-viewport"
            style={{ width: size, height: size }}
            tabIndex={0}
            role="group"
            aria-label="사진 자르기. 끌어서 옮기고, 모서리를 끌어 영역을 조절합니다. 키보드: 화살표로 옮기기, +와 -로 확대·축소"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onKeyDown={onKeyDown}
          >
            {img && view && crop && (
              <>
                <img
                  src={img.src}
                  alt=""
                  draggable={false}
                  style={{ width: w, height: h, transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.k})`, transition }}
                />
                <div
                  className="crop-frame"
                  style={{ left: view.tx + view.k * crop.x, top: view.ty + view.k * crop.y, width: view.k * crop.s, height: view.k * crop.s, transition }}
                >
                  {(["nw", "ne", "sw", "se"] as const).map((c) => (
                    <span key={c} className={`crop-handle ${c}`} data-corner={c} />
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="crop-controls">
            <label htmlFor="crop-zoom" className="sr-only">
              확대
            </label>
            <span aria-hidden="true">−</span>
            <input
              id="crop-zoom"
              type="range"
              min={0}
              max={1000}
              value={slider}
              disabled={!crop || maxZoom <= 1}
              onChange={(e) => zoomTo(minDim / Math.pow(maxZoom, Number(e.target.value) / 1000))}
            />
            <span aria-hidden="true">+</span>
          </div>
          <div className="crop-preview">
            <canvas ref={previewRef} width={96} height={96} aria-label="저장될 모습 미리보기" role="img" />
            <span>{AVATAR_SIZE}×{AVATAR_SIZE}으로 저장됩니다</span>
          </div>
        </>
      )}
      <div className="buttons">
        <button type="button" className="btn" onClick={onCancel}>
          취소
        </button>
        <button type="button" className="btn btn-primary" onClick={save} disabled={!crop || saving}>
          {saving ? "저장 중…" : "저장"}
        </button>
      </div>
    </div>
  );
}
